/**
 * The words on the access request queue (LO-210).
 *
 * Plain JavaScript, no React, so what the dialogs say can be held to what
 * they must say by a test that runs in a second. The kernel decides who may
 * approve and what a grant does; this only says it in sentences, and where the
 * kernel has its own sentence for what an act did not prove (`does_not_prove`)
 * the outcome reads that out rather than writing a second one.
 *
 * The dialog that confirms a grant is the place on this screen where a block
 * of qualification belongs, because it is only read at the moment somebody
 * changes something. Everywhere else a qualification goes on the figure it
 * qualifies.
 */

/** The four ways to look at the queue, in the order they are offered. `id` is
 *  what `GET /access-requests?state=` takes. */
export const STATE_FILTERS = [
  { id: "pending", label: "Waiting" },
  { id: "approved", label: "Approved" },
  { id: "declined", label: "Declined" },
  { id: "all", label: "All" },
];

/**
 * Where a mark on an access list sends somebody. Addresses only: the screens
 * at the other end read the parameters, and nothing here decides whether the
 * person following the link may do what it asks.
 */
export const ACCESS_LINKS = {
  grant: ({ user, tag }) => `#people?${new URLSearchParams({ user, tag })}`,
  request: ({ id }) => `#access-requests?${new URLSearchParams({ request: id })}`,
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** How long ago, in words, and the date once it is old. Empty when there is no
 *  usable time, never a guess. */
export function whenSentence(iso, now = Date.now()) {
  const at = Date.parse(iso || "");
  if (!Number.isFinite(at)) return "";
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours === 1 ? "an hour ago" : `${hours} hours ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} ${days === 1 ? "day" : "days"} ago`;
  const date = new Date(at);
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/**
 * What the approve dialog says. `lines` are the standing caveats; `note` is the
 * person's own words, kept apart so it is shown as theirs and never mistaken
 * for the kernel's.
 */
export function approveCopy(request) {
  const { requested_by: who, tag, app_type: app } = request;
  return {
    title: `Give ${who} the ${tag} tag?`,
    confirm: `Give ${who} ${tag}`,
    note: String(request.note || "").trim(),
    lines: [
      `${who} asked for ${tag} through ${app}.`,
      `Approving gives them ${tag} everywhere, not only for ${app}: every app that reads records tagged ${tag} will show those records to them.`,
      `It is the same grant as adding ${tag} on the People screen, recorded under your name with this request as the reason. It can be taken back there.`,
      `It does not prove the request is a good one: the kernel records that you approved it and cannot judge whether ${who} should see these records.`,
      "They may need to sign in again before the app opens, because a token already issued keeps the groups it had, for up to an hour.",
    ],
  };
}

/** What the decline dialog says. It does not promise the person is told the
 *  reason: the reason is kept on the request and in the log, and the person's
 *  own view carries the state. */
export function declineCopy(request) {
  const { requested_by: who, tag } = request;
  return {
    title: `Decline ${who}'s request for ${tag}?`,
    confirm: "Decline request",
    reasonLabel: "Why you are declining",
    lines: [
      `Declining grants nothing and takes nothing away. The request closes as declined, with your reason kept on it and in the log, and ${who} may ask again.`,
    ],
  };
}

/** The banner after an act: what happened, then what the kernel says it did not
 *  prove. */
export function outcomeOf(action, body) {
  const { requested_by: who, tag } = body;
  const lines = Array.isArray(body.does_not_prove) ? body.does_not_prove : [];
  if (action === "approve") {
    return {
      severity: "success",
      title: body.result === "already_held"
        ? `${who} already held ${tag}, so nothing changed`
        : `Gave ${who} ${tag}`,
      lines,
    };
  }
  return { severity: "success", title: `Declined ${who}'s request for ${tag}`, lines };
}

/** Zero rows from a read that succeeded: information about the queue, and about
 *  what an empty one does not say. */
export function emptyCopy({ scope, state }) {
  if (state !== "pending") {
    return {
      title: state === "all" ? "No requests at all" : `No ${state} requests`,
      text: scope === "own"
        ? "The kernel holds none of yours in this state. Asking is done from the app that refused you."
        : "The kernel holds none in this state. A decided request stays findable here, so this is not a log that forgot.",
    };
  }
  if (scope === "own") {
    return {
      title: "You have no request waiting",
      text: "You see only your own requests here, because you may not grant tags. Asking is done from the app that refused you.",
    };
  }
  return {
    title: "Nobody is waiting for access",
    text: "A request appears here when a person is refused an app because they lack a tag it needs, and asks for it. An empty queue is not a refusal and says nothing about whether anybody is being turned away: the Apps screen marks the people who would be.",
  };
}

/** Why Approve and Decline are off for a request, or an empty string when they
 *  are on. The two halves are the kernel's: may this person decide at all, and
 *  may this app hand out that tag. */
export function whyDisabled({ may_decide: mayDecide, can_decide: canDecide }, request, governors = []) {
  if (!mayDecide) {
    const names = governors.length ? ` (${governors.join(", ")})` : "";
    return `You may not decide requests: that is for the governors${names}.`;
  }
  if (!canDecide) {
    return `This app does not declare the ${request.tag} tag, so it may not hand it out or refuse it. Decide it from an app that does.`;
  }
  return "";
}
